// The browser side of a server-run tailoring job. The server (_shared/tailor-run.ts) does the
// work; this starts it and follows its progress. Starting the same run again (after
// a reload) reattaches to it, so nothing re-runs and nothing is paid twice.
import { supabase } from '@/integrations/supabase/client';

const RUN_URL = 'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd?run=1';
const POLL_MS = 3000;
const MAX_POLL_FAILURES = 40; // about two minutes of lost connection

export interface TailorRunInput {
  base: Record<string, unknown>;
  highlight_count: unknown;
  run_id?: string;
  run_label?: string;
}
export interface TailorRunProgress { step: string; activeSteps: string[]; doneSteps: string[] }
export interface TailorRunResult { data: any; usage: Array<{ stage: string; seconds: number; calls: any[] }> }

const hash = (s: string) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

async function post(body: Record<string, unknown>): Promise<any> {
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  if (!token) throw new Error('You are not signed in.');
  const res = await fetch(RUN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: (supabase as any).supabaseKey },
    body: JSON.stringify(body),
  });
  const j = await res.json().catch(() => null);
  if (!j?.success) throw new Error(j?.error || `Tailoring could not start (HTTP ${res.status})`);
  return j;
}

/** The key covers only the job itself, so a reload of the same run finds it again. */
export async function startTailorRun(localRunId: string, input: TailorRunInput): Promise<string> {
  const j = await post({ action: 'start', client_key: `${localRunId}:${hash(JSON.stringify(input.base))}`, input });
  return j.id;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Follows the run until it ends. Stopping cancels it on the server. */
export async function watchTailorRun(id: string, signal: AbortSignal | undefined, onProgress: (p: TailorRunProgress) => void): Promise<TailorRunResult> {
  let failures = 0;
  for (;;) {
    if (signal?.aborted) {
      await post({ action: 'cancel', id }).catch((e) => console.error('Cancel failed:', e));
      throw Object.assign(new Error('Tailoring stopped by the user'), { cancelled: true });
    }
    const { data: row, error } = await (supabase as any).from('tailor_runs')
      .select('status,step,active_steps,done_steps,error,lease_until').eq('id', id).single();
    if (error || !row) {
      if (++failures >= MAX_POLL_FAILURES) throw new Error('We lost touch with your tailoring run.');
      await wait(POLL_MS);
      continue;
    }
    failures = 0;
    onProgress({ step: row.step, activeSteps: row.active_steps ?? [], doneSteps: row.done_steps ?? [] });

    if (row.status === 'done') {
      const { data: fin, error: finErr } = await (supabase as any).from('tailor_runs').select('result,usage').eq('id', id).single();
      if (finErr || !fin?.result) throw new Error('Your resume finished but could not be loaded.');
      return { data: fin.result, usage: fin.usage ?? [] };
    }
    if (row.status === 'failed') throw new Error(row.error || 'Tailoring stopped before your resume was finished.');
    if (row.status === 'cancelled') throw Object.assign(new Error('Tailoring stopped by the user'), { cancelled: true });

    // A step's worker died: its lease ran out with no update.
    if (row.lease_until && new Date(row.lease_until).getTime() < Date.now()) {
      await post({ action: 'nudge', id }).catch((e) => console.error('Nudge failed:', e));
    }
    await wait(POLL_MS);
  }
}
